/**
 * Utilidades para normalizar datos de API a formato de Google Charts.
 */

/**
 * Extrae el valor numérico útil de un campo del dashboard.
 * Las APIs devuelven 3 formas: número directo, objeto { total, porcentaje, importe }, o array de objetos { label, total }.
 *
 * @param {unknown} val
 * @returns {number}
 */
export function extractChartValue(val) {
    if (val === null || val === undefined) return 0;
    if (typeof val === "number") return val;
    if (
        typeof val === "object" &&
        !Array.isArray(val) &&
        typeof val.total === "number"
    )
        return val.total;
    return 0;
}

/**
 * Normaliza un objeto clave/valor al formato de datos de Google Charts.
 * Soporta 4 formas del API:
 *
 * 1. Array de { label, total } — geografía (provincias, localidades, códigos_postales)
 * 2. Objeto con valores anidados { total, porcentaje, importe } — metodos_pago, canales_venta, ...
 * 3. Objeto con valores array de { label, total } — compradores_geografia
 * 4. Objeto clave/valor plano — rango_edad, rango_edad_asistencia
 *
 * @param {object|Array|null|undefined} obj
 * @param {string} labelCol — nombre de la columna de etiquetas
 * @param {string} valueCol — nombre de la columna de valores
 * @returns {Array} — array en formato Google Charts: [[labelCol, valueCol], [clave, valor], ...]
 */
export function objectToChartData(
    obj,
    labelCol = "Concepto",
    valueCol = "Total",
) {
    if (!obj || typeof obj !== "object") return [];

    // Forma 1: array de { label, total } (geografía)
    if (Array.isArray(obj)) {
        const rows = [[labelCol, valueCol]];
        for (const item of obj) {
            if (item && typeof item === "object" && "total" in item) {
                rows.push([item.label || item.name || "", item.total ?? 0]);
            }
        }
        return rows.length > 1 ? rows : [];
    }

    const entries = Object.entries(obj).filter(([, v]) => v != null);
    if (entries.length === 0) return [];

    const firstValue = entries[0]?.[1];
    const isValueObject = firstValue && typeof firstValue === "object";

    if (isValueObject) {
        // Forma 3: valores son arrays de { label, total } → compradores_geografia
        if (Array.isArray(firstValue)) {
            for (const [, val] of entries) {
                if (
                    Array.isArray(val) &&
                    val.length > 0 &&
                    typeof val[0] === "object" &&
                    "total" in val[0]
                ) {
                    const rows = [[labelCol, valueCol]];
                    for (const item of val) {
                        rows.push([item.label || item.name || "", item.total ?? 0]);
                    }
                    return rows.length > 1 ? rows : [];
                }
            }
            return [];
        }

        // Forma 2: valores son objetos con 'total' → metodos_pago, canales_venta, etc.
        if (typeof firstValue.total === "number") {
            const rows = [[labelCol, valueCol]];
            for (const [key, val] of entries) {
                if (val && typeof val.total === "number") {
                    rows.push([key, val.total]);
                }
            }
            return rows.length > 1 ? rows : [];
        }

        return [];
    }

    // Forma 4: clave/valor plano → rango_edad, rango_edad_asistencia
    const rows = [[labelCol, valueCol]];
    for (const [key, value] of entries) {
        if (typeof value === "number") {
            rows.push([key, value]);
        }
    }
    return rows.length > 1 ? rows : [];
}
